import { useMemo } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getQuotes,
  getQuote,
  getQuoteByNumber,
  getQuoteByMongoId,
  getQuoteSkus,
  getQuoteOperationsAuditLogs,
  getQuoteStats,
  getCustomerOrganisations,
  searchQuoteAccountsPaginated,
  getQuoteAccountById,
  getQuoteV3,
  createQuote,
  createQuoteForNewCustomer,
  updateQuote,
  deleteQuote,
  getSalesUsers,
  getOrganisationUsers,
  addContractualManagerTag,
  getSubscriptionsByCustomerForQuote,
  getSitesByCustomer,
  getSitesForAccount,
  getTicketsByCustomer,
  getSkuCatalog,
  searchSkuCatalogPaginated,
  getPricelists,
  getSubscriptionsByCustomer,
  getInvoicesByQuoteNumber,
  getNextQuoteNumber,
  getDistinctProjectNumbers,
  addQuoteNote,
  addQuoteOperationsNote,
  editQuoteNote,
  deleteQuoteNote,
  updateHubspotDeal,
  getQuoteChangelog,
  uploadPoFile,
  deletePoFile,
  getSensorsByQuote,
  getSensorsByInvoices,
  getSelectableSensors,
  getSelectableSensorsByAccount,
  getSensorFilterOptions,
  addSensorToQuote,
  removeSensorFromQuote,
  getTransmittersByQuote,
  ensureQuoteMongo,
  materializeQuoteFromMongo,
  discardQuote,
} from '../api';
import { getQuoteV4ForEdit } from '@/features/accounts/api';
import type { SalesManager } from '@/features/accounts/api';
import type { V4QuoteFormDetail } from '@/features/accounts/types';
import type { AddQuoteNoteInput, Quote, QuoteSensor, SensorSelectParams, V3Quote } from '../types';
import { quoteKeys } from './queryKeys';
import type {
  QuotesFilters,
  QuotesResponse,
  CreateQuoteInput,
  CreateQuoteForNewCustomerInput,
  UpdateQuoteInput,
} from '../types';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

function asQuoteSensorArray(data: unknown): QuoteSensor[] {
  return Array.isArray(data) ? data : [];
}

function getPopulatedUserName(user: V3Quote['sales_manager']): string | null | undefined {
  if (!user || typeof user === 'string') return undefined;
  const name = [user.firstname, user.lastname].filter(Boolean).join(' ').trim();
  return name || user.email || null;
}

function getPopulatedUserId(user: V3Quote['sales_manager']): string | null | undefined {
  if (typeof user === 'string') return user;
  return user?._id;
}

function getSalesManagerNameFromCache(
  salesManagerId: string | null | undefined,
  salesManagers: SalesManager[] | undefined,
): string | undefined {
  if (!salesManagerId) return undefined;
  return salesManagers?.find((manager) => (
    manager.mongoUserId === salesManagerId || manager.id === salesManagerId
  ))?.name;
}

function buildQuoteListPatch(
  updatedQuote: V3Quote,
  submittedData: UpdateQuoteInput,
  salesManagers?: SalesManager[],
): Partial<Quote> {
  const nextShippingAddress = updatedQuote.shipping_addresses?.[0]?.address
    ?? submittedData.shipping_addresses?.[0]?.address;
  const salesManagerId = getPopulatedUserId(updatedQuote.sales_manager);
  const contractualManagerId = getPopulatedUserId(updatedQuote.contractual_manager);
  const salesManagerName = getPopulatedUserName(updatedQuote.sales_manager)
    ?? getSalesManagerNameFromCache(salesManagerId, salesManagers);
  const contractualManagerName = getPopulatedUserName(updatedQuote.contractual_manager);

  const patch: Partial<Quote> = {};

  // V4-native updates return `_id` = PG UUID (no Mongo backing doc); only
  // carry it over when it's a real 24-char Mongo ObjectId, otherwise we'd
  // clobber the list row's mongoQuoteId (used for the delete/cache match).
  if (updatedQuote._id && /^[0-9a-f]{24}$/i.test(updatedQuote._id)) {
    patch.mongoQuoteId = updatedQuote._id;
  }

  if (updatedQuote.quote_number) patch.quoteNumber = updatedQuote.quote_number;
  if (updatedQuote.status) patch.status = updatedQuote.status;
  if ('project_name' in updatedQuote) patch.projectName = updatedQuote.project_name ?? null;
  if ('description' in updatedQuote) patch.description = updatedQuote.description ?? null;
  if ('date' in updatedQuote) patch.date = updatedQuote.date ?? null;
  if ('expiration_date' in updatedQuote) patch.expirationDate = updatedQuote.expiration_date ?? null;
  if ('accepted_at' in updatedQuote) patch.acceptedDate = updatedQuote.accepted_at ?? null;
  if ('estimated_delivery' in updatedQuote) patch.estimatedDelivery = updatedQuote.estimated_delivery ?? null;
  if ('updated_at' in updatedQuote) patch.updatedAt = updatedQuote.updated_at ?? null;
  if (updatedQuote.total != null || submittedData.total != null) {
    patch.total = String(updatedQuote.total ?? submittedData.total);
  }
  if (updatedQuote.currency) patch.currency = updatedQuote.currency;
  if ('tax_category' in updatedQuote) patch.taxCategory = updatedQuote.tax_category ?? null;
  if (salesManagerId !== undefined) patch.salesManagerId = salesManagerId;
  if (contractualManagerId !== undefined) patch.contractualManagerId = contractualManagerId;
  if (salesManagerName !== undefined) patch.salesManagerName = salesManagerName;
  if (contractualManagerName !== undefined) patch.contractualManagerName = contractualManagerName;
  if ('hubspot_link' in updatedQuote || submittedData.hubspot_link !== undefined) {
    patch.hubspotLink = updatedQuote.hubspot_link ?? submittedData.hubspot_link ?? null;
  }
  if ('gdrive_link' in updatedQuote || submittedData.gdrive_link !== undefined) {
    patch.gdriveLink = updatedQuote.gdrive_link ?? submittedData.gdrive_link ?? null;
  }
  if ('po_number' in updatedQuote || submittedData.po_number !== undefined) {
    patch.poNumber = updatedQuote.po_number ?? submittedData.po_number ?? null;
  }
  if (nextShippingAddress !== undefined) patch.shippingAddress = nextShippingAddress;
  if ('project_number' in updatedQuote || submittedData.project_number !== undefined) {
    patch.projectNumber = updatedQuote.project_number ?? submittedData.project_number ?? null;
  }
  if ('dealtype' in updatedQuote || submittedData.dealtype !== undefined) {
    patch.dealType = updatedQuote.dealtype ?? submittedData.dealtype ?? null;
  }
  if ('next_step' in updatedQuote || submittedData.next_step !== undefined) {
    patch.nextStep = updatedQuote.next_step ?? submittedData.next_step ?? null;
  }
  if ('next_step_deadline' in updatedQuote || submittedData.next_step_deadline !== undefined) {
    patch.nextStepDeadline = updatedQuote.next_step_deadline ?? submittedData.next_step_deadline ?? null;
  }
  if (updatedQuote.invoice_numbers || submittedData.invoice_numbers) {
    patch.invoiceNumbers = updatedQuote.invoice_numbers ?? submittedData.invoice_numbers ?? null;
  }
  if (updatedQuote.tracking_numbers || submittedData.tracking_numbers) {
    patch.trackingNumbers = updatedQuote.tracking_numbers ?? submittedData.tracking_numbers ?? null;
  }
  if ('billing_address' in updatedQuote || submittedData.billing_address !== undefined) {
    patch.billingAddress = updatedQuote.billing_address ?? submittedData.billing_address ?? null;
  }
  if ('po_file' in updatedQuote || submittedData.po_file !== undefined) {
    patch.poFile = updatedQuote.po_file ?? submittedData.po_file ?? null;
  }

  return patch;
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch quotes with filters
 */
export function useQuotes(filters: QuotesFilters = {}) {
  return useQuery({
    queryKey: quoteKeys.list(filters),
    queryFn: () => getQuotes(filters),
  });
}

/**
 * Hook to fetch a single quote by ID
 */
export function useQuote(quoteId: string | undefined) {
  return useQuery({
    queryKey: quoteKeys.detail(quoteId!),
    queryFn: () => getQuote(quoteId!),
    enabled: !!quoteId,
  });
}

/**
 * Hook to fetch a single quote by quote number
 */
export function useQuoteByNumber(quoteNumber: string | undefined) {
  return useQuery({
    queryKey: quoteKeys.byNumber(quoteNumber!),
    queryFn: () => getQuoteByNumber(quoteNumber!),
    enabled: !!quoteNumber,
  });
}

/**
 * Hook to fetch a single quote by MongoDB ObjectId via V4
 */
export function useQuoteByMongoId(mongoQuoteId: string | null | undefined) {
  return useQuery({
    queryKey: [...quoteKeys.details(), 'byMongoId', mongoQuoteId] as const,
    queryFn: () => getQuoteByMongoId(mongoQuoteId!),
    enabled: !!mongoQuoteId,
  });
}

/**
 * Hook to fetch V3 quote (full data for edit form)
 */
export function useQuoteV3(quoteId: string | null | undefined) {
  return useQuery({
    queryKey: quoteKeys.v3detail(quoteId!),
    queryFn: () => getQuoteV3(quoteId!),
    enabled: !!quoteId,
  });
}

/**
 * Hook to fetch V4 quote (full data for edit form, from PostgreSQL)
 */
export function useQuoteV4(pgQuoteId: string | null | undefined) {
  return useQuery({
    queryKey: ['quote-v4-detail', pgQuoteId],
    queryFn: () => getQuoteV4ForEdit(pgQuoteId!),
    enabled: !!pgQuoteId,
  });
}

/**
 * Hook to fetch SKUs for a quote
 */
export function useQuoteSkus(quoteId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: quoteKeys.skus(quoteId!),
    queryFn: () => getQuoteSkus(quoteId!),
    enabled: !!quoteId && enabled,
  });
}

export function useQuoteOperationsAuditLogs(
  quoteId: string | undefined,
  params: { pageNumber?: number; pageLength?: number } = {}
) {
  return useQuery({
    queryKey: quoteKeys.operationsAuditLogs(quoteId!, params),
    queryFn: () => getQuoteOperationsAuditLogs(quoteId!, params),
    enabled: !!quoteId,
  });
}

/**
 * Hook to fetch quote stats by status
 */
export function useQuoteStats(
  filters: Partial<QuotesFilters> = {}
) {
  return useQuery({
    queryKey: quoteKeys.stats(filters),
    queryFn: () => getQuoteStats(filters),
    staleTime: 60 * 1000, // Cache for 1 minute
  });
}

/**
 * Hook to fetch customer organisations for filter dropdown
 */
export function useCustomerOrganisations() {
  return useQuery({
    queryKey: quoteKeys.customers(),
    queryFn: getCustomerOrganisations,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

const ACCOUNT_PAGE_SIZE = 50;

/**
 * Paginated account search with infinite scrolling.
 * Used by the Quotes account filter combobox.
 */
export function useQuoteAccountsInfinite(search: string) {
  return useInfiniteQuery({
    queryKey: quoteKeys.accountsInfinite(search),
    queryFn: ({ pageParam = 0 }) =>
      searchQuoteAccountsPaginated({
        search,
        pageNumber: pageParam,
        pageLength: ACCOUNT_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.pageNumber + 1) * lastPage.pageLength;
      return loaded < lastPage.total ? lastPage.pageNumber + 1 : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Look up a single account by id.
 * Used to resolve the selected-account label when it's not in the loaded pages.
 */
export function useQuoteAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: quoteKeys.account(accountId ?? ''),
    queryFn: () => getQuoteAccountById(accountId!),
    enabled: !!accountId && accountId !== 'all',
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch sales users for dropdowns
 */
export function useSalesUsers() {
  return useQuery({
    queryKey: quoteKeys.salesUsers(),
    queryFn: getSalesUsers,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch users for a specific organisation
 */
export function useOrganisationUsers(orgId: string | undefined, tags?: string[], search?: string) {
  return useQuery({
    queryKey: [...quoteKeys.orgUsers(orgId!), ...(tags || []), search || ''] as const,
    queryFn: () => getOrganisationUsers(orgId!, tags, search),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Tag an existing organisation contact as Contractual Manager (#1121).
 * Toasts are raised by the caller so the dialog can keep itself open on failure.
 */
export function useAddContractualManagerTag() {
  return useMutation({
    mutationFn: ({ userId, orgId }: { userId: string; orgId: string }) =>
      addContractualManagerTag(userId, orgId),
  });
}

/**
 * Hook to fetch subscriptions (V4 contracts) for a customer (for quote form dropdown)
 * @param customerId - MongoDB organisation ObjectId (used as cache key + fallback)
 * @param pgAccountId - PostgreSQL account UUID (used for server-side filtering)
 */
export function useSubscriptionsByCustomerForQuote(customerId: string | undefined, pgAccountId?: string) {
  return useQuery({
    queryKey: [...quoteKeys.all, 'subscriptions-for-quote', pgAccountId || customerId] as const,
    queryFn: () => getSubscriptionsByCustomerForQuote(customerId!, pgAccountId),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

/** @deprecated Use useSubscriptionsByCustomerForQuote instead */
export function useContractsByCustomer(customerId: string | undefined, pgAccountId?: string) {
  return useSubscriptionsByCustomerForQuote(customerId, pgAccountId);
}

/**
 * Hook to fetch sites for a customer (legacy Mongo-shaped passthrough).
 */
export function useSitesByCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: [...quoteKeys.all, 'sites', customerId] as const,
    queryFn: () => getSitesByCustomer(customerId!),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch a customer's sites through the V4 sites API (account-scoped,
 * camelCase). The quote form uses this so its shape survives the sites
 * PG-native flip; `mongoSiteId` stays the id the quote payload stores.
 */
export function useAccountSites(pgAccountId: string | undefined) {
  return useQuery({
    queryKey: [...quoteKeys.all, 'sites', pgAccountId] as const,
    queryFn: () => getSitesForAccount(pgAccountId!),
    enabled: !!pgAccountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch tickets for a customer
 */
export function useTicketsByCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: [...quoteKeys.all, 'tickets', customerId] as const,
    queryFn: () => getTicketsByCustomer(customerId!),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch SKU catalog
 */
export function useSkuCatalog() {
  return useQuery({
    queryKey: [...quoteKeys.all, 'skuCatalog'] as const,
    queryFn: getSkuCatalog,
    staleTime: 10 * 60 * 1000,
  });
}

const SKU_PAGE_SIZE = 50;

/**
 * Paginated SKU catalog search with infinite scrolling.
 * Used by the quote SKU combobox.
 */
export function useSkuCatalogInfinite(search: string) {
  return useInfiniteQuery({
    queryKey: quoteKeys.skuCatalogInfinite(search),
    queryFn: ({ pageParam = 0 }) =>
      searchSkuCatalogPaginated({
        search,
        pageNumber: pageParam,
        pageLength: SKU_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.pageNumber + 1) * lastPage.pageLength;
      return loaded < lastPage.total ? lastPage.pageNumber + 1 : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch pricelists
 */
export function usePricelists() {
  return useQuery({
    queryKey: quoteKeys.pricelists(),
    queryFn: getPricelists,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch subscriptions for a customer
 */
export function useSubscriptionsByCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: quoteKeys.subscriptions(customerId!),
    queryFn: () => getSubscriptionsByCustomer(customerId!),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch invoices linked to a quote number
 */
export function useInvoicesByQuoteNumber(quoteNumber: string | undefined) {
  return useQuery({
    queryKey: quoteKeys.invoices(quoteNumber!),
    queryFn: () => getInvoicesByQuoteNumber(quoteNumber!),
    enabled: !!quoteNumber,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to fetch the next available quote number
 */
export function useNextQuoteNumber(enabled = true) {
  return useQuery({
    queryKey: quoteKeys.nextQuoteNumber(),
    queryFn: getNextQuoteNumber,
    enabled,
    staleTime: 0,
  });
}

/**
 * Hook to fetch distinct project numbers for combobox suggestions
 */
export function useDistinctProjectNumbers() {
  return useQuery({
    queryKey: [...quoteKeys.all, 'projectNumbers'] as const,
    queryFn: getDistinctProjectNumbers,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new quote
 */
export function useCreateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateQuoteInput) => createQuote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.stats() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.nextQuoteNumber() });
      toast.success('Quote created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create quote', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to create a header-only PG-native quote for a brand-new customer.
 * A V1 Mongo org + a V2 PG account are auto-created server-side (flag-gated).
 */
export function useCreateQuoteForNewCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateQuoteForNewCustomerInput) => createQuoteForNewCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.stats() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.nextQuoteNumber() });
      toast.success('Quote created for new customer');
    },
    onError: (error) => {
      toast.error('Failed to create quote', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update a quote
 *
 * Note: Quote updates go to MongoDB (V3 API), but the list displays data from PostgreSQL (V4 API).
 * The CDC sync (accounts_sync) propagates changes from MongoDB → PostgreSQL, but there's a delay.
 * To avoid showing stale data, we optimistically update the V4 cache immediately after mutation success.
 */
export function useUpdateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateQuoteInput }) => updateQuote(id, data),
    onSuccess: (updatedV3Quote, variables) => {
      // Optimistically update all V4 list caches with the new data
      // This bridges the gap between MongoDB update and PostgreSQL CDC sync
      const salesManagers = queryClient.getQueryData<SalesManager[]>(['sales-managers']);
      const listPatch = buildQuoteListPatch(updatedV3Quote, variables.data, salesManagers);
      // `variables.id` is now the PG UUID for V4-native updates (and still the
      // Mongo ObjectId for the legacy fallback), so match caches on EITHER id.
      queryClient.setQueriesData<V4QuoteFormDetail>(
        { queryKey: ['quote-v4-detail'] },
        (oldData) => {
          if (!oldData || (oldData.id !== variables.id && oldData.mongoQuoteId !== variables.id)) return oldData;
          return {
            ...oldData,
            salesManagerId: listPatch.salesManagerId ?? oldData.salesManagerId,
            salesManagerName: listPatch.salesManagerName ?? oldData.salesManagerName,
          };
        },
      );
      queryClient.setQueriesData<QuotesResponse>(
        { queryKey: quoteKeys.lists() },
        (oldData) => {
          if (!oldData?.quotes) return oldData;
          return {
            ...oldData,
            quotes: oldData.quotes.map((quote) => {
              if (quote.id === variables.id || quote.mongoQuoteId === variables.id) {
                return {
                  ...quote,
                  ...listPatch,
                  salesManagerName: listPatch.salesManagerName ?? quote.salesManagerName,
                  contractualManagerName: listPatch.contractualManagerName ?? quote.contractualManagerName,
                };
              }
              return quote;
            }),
          };
        }
      );

      // Still invalidate to get fresh data from backend.
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.stats() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.details() });
      // The edit form PREFERS the V3 (Mongo) detail (QuoteFormModal), which is
      // keyed by the MONGO id — but `variables.id` is now the PG UUID (updates
      // route to /v4/quotes/:id). Invalidating only v3detail(variables.id) would
      // MISS the Mongo-keyed cache, so the reopened form shows a stale status.
      // Invalidate by the response's Mongo id AND broadly across all v3 details.
      queryClient.invalidateQueries({ queryKey: quoteKeys.v3detail(variables.id) });
      if (updatedV3Quote?._id) {
        queryClient.invalidateQueries({ queryKey: quoteKeys.v3detail(updatedV3Quote._id) });
      }
      queryClient.invalidateQueries({ queryKey: quoteKeys.v3details() });
      // useQuoteV4 (the edit-form's detail query) is mounted at the QuotesPage level, so it is
      // always "active"; refetchType:'inactive' skipped it and the reopened form showed a stale
      // status until a hard refresh. Refetch it so saved edits appear immediately on reopen.
      queryClient.invalidateQueries({ queryKey: ['quote-v4-detail'] });
      toast.success('Quote updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update quote', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete a quote
 */
type DeleteQuoteVariables = string | {
  mongoQuoteId?: string;
  pgQuoteId?: string;
};

export function useDeleteQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: DeleteQuoteVariables) => {
      const mongoQuoteId = typeof variables === 'string' ? variables : variables.mongoQuoteId;
      const pgQuoteId = typeof variables === 'string' ? undefined : variables.pgQuoteId;
      // Prefer the Mongo id so the legacy V1 app drops the quote too; fall back to
      // the PG UUID, which `deleteQuote` self-routes to the native V4 soft delete.
      // Quotes on accounts with no legacy Mongo organisation only ever have the
      // latter — without the fallback they posted a literal "undefined" id to the
      // legacy route.
      const targetQuoteId = mongoQuoteId || pgQuoteId;
      if (!targetQuoteId) {
        throw new Error('deleteQuote requires a Mongo quote id or a PostgreSQL quote id');
      }
      return deleteQuote(targetQuoteId);
    },
    onSuccess: (_result, variables) => {
      const mongoQuoteId = typeof variables === 'string' ? variables : variables.mongoQuoteId;
      const pgQuoteId = typeof variables === 'string' ? undefined : variables.pgQuoteId;

      queryClient.setQueriesData<QuotesResponse>({ queryKey: quoteKeys.lists() }, (oldData) => {
        if (!oldData?.quotes) return oldData;

        const quotes = oldData.quotes.filter((quote) => (
          quote.id !== pgQuoteId && quote.mongoQuoteId !== mongoQuoteId
        ));

        if (quotes.length === oldData.quotes.length) return oldData;

        return {
          ...oldData,
          quotes,
          total: Math.max(0, oldData.total - (oldData.quotes.length - quotes.length)),
        };
      });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.stats() });
      toast.success('Quote deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete quote', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to discard a quote by setting its status to `cancelled` via the native
 * V4 PUT endpoint so the change lands in PostgreSQL. Use this instead of
 * useUpdateQuote for discard actions — the legacy update path does not propagate
 * to the PG Kanban.
 */
export function useDiscardQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pgQuoteId: string) => discardQuote(pgQuoteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.stats() });
      toast.success('Quote discarded');
    },
    onError: (error) => {
      toast.error('Failed to discard quote', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Lazy-materialize a legacy MongoDB quote into PostgreSQL.
 *
 * Use this BEFORE allowing edit on a quote that exists in Mongo but has
 * no PG row yet (or whose PG row may be stale because `accounts_sync`
 * stopped before it could CDC the latest Mongo state). The endpoint is
 * idempotent — calling on an already-materialized quote returns the
 * existing PG row untouched (`materialized: false`).
 *
 * After success, the source Mongo doc is flagged `migrated_to_pg: true`
 * and the V3 PUT route refuses further writes (423 Locked) — V4 becomes
 * the sole write surface for that quote.
 *
 * Invalidates the lists, the per-account quote lists, and the V4 detail
 * cache so the freshly-materialized row shows up everywhere immediately.
 */
export function useMaterializeQuoteFromMongo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mongoQuoteId: string) => materializeQuoteFromMongo(mongoQuoteId),
    onSuccess: (result) => {
      const pgQuoteId = result.quote.id;
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['account-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard'] });
      if (pgQuoteId) {
        queryClient.invalidateQueries({ queryKey: ['quote-v4-detail', pgQuoteId] });
      }
    },
    onError: (error) => {
      toast.error('Failed to import quote from legacy system', {
        description: getErrorMessage(error),
      });
    },
  });
}

/**
 * Ensure a quote has a valid MongoDB backing document before V3 mutations.
 */
export function useEnsureQuoteMongo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (quoteId: string) => ensureQuoteMongo(quoteId),
    onSuccess: (_result, quoteId) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.details() });
      queryClient.invalidateQueries({ queryKey: ['quote-v4-detail', quoteId] });
    },
    onError: (error) => {
      toast.error('Failed to prepare quote for editing', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// Note Mutation Hooks
// =============================================================================

export function useAddQuoteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, data }: { quoteId: string; data: AddQuoteNoteInput }) =>
      addQuoteNote(quoteId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.v3detail(variables.quoteId) });
      toast.success('Note added');
    },
    onError: (error) => {
      toast.error('Failed to add note', { description: getErrorMessage(error) });
    },
  });
}

// Append an internal operations note (PG operations_internal_notes) for a quote.
// Toasts are handled by the caller (which has i18n `t`); this only refreshes the
// quote detail so the new note appears in the ops panel.
export function useAddQuoteOperationsNote(quoteId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (content: string) => addQuoteOperationsNote(quoteId!, content),
    onSuccess: () => {
      if (quoteId) queryClient.invalidateQueries({ queryKey: quoteKeys.detail(quoteId) });
    },
  });
}

export function useEditQuoteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, noteId, content }: { quoteId: string; noteId: string; content: string }) =>
      editQuoteNote(quoteId, noteId, content),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.v3detail(variables.quoteId) });
      toast.success('Note updated');
    },
    onError: (error) => {
      toast.error('Failed to update note', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteQuoteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, noteId }: { quoteId: string; noteId: string }) =>
      deleteQuoteNote(quoteId, noteId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.v3detail(variables.quoteId) });
      toast.success('Note deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete note', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// HubSpot Mutation Hooks
// =============================================================================

/**
 * Sync the quote onto the HubSpot deal it is already linked to.
 *
 * ATE-11441 D1/D2: the button that calls this is labelled "sync", and it must only
 * ever update - the create route now answers 409 and no create primitive exists.
 * `hsId` is parsed out of the quote's hubspot_link, never defaulted.
 */
export function useUpdateHubspotDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, hsId }: { quoteId: string; hsId: string }) =>
      updateHubspotDeal(quoteId, hsId),
    onSuccess: (_, { quoteId }) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.v3detail(quoteId) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      toast.success('HubSpot deal synced');
    },
    onError: (error) => {
      toast.error('Failed to sync HubSpot deal', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// PO File Mutation Hooks
// =============================================================================

export function useUploadPoFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, file }: { quoteId: string; file: File }) => uploadPoFile(quoteId, file),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.v3detail(variables.quoteId) });
      toast.success('File uploaded');
    },
    onError: (error) => {
      toast.error('Failed to upload file', { description: getErrorMessage(error) });
    },
  });
}

export function useDeletePoFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (quoteId: string) => deletePoFile(quoteId),
    onSuccess: (_data, quoteId) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.v3detail(quoteId) });
      toast.success('File deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete file', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// Changelog Query Hook
// =============================================================================

export function useQuoteChangelog(quoteId: string | null | undefined) {
  return useQuery({
    queryKey: quoteKeys.changelog(quoteId!),
    queryFn: () => getQuoteChangelog(quoteId!),
    enabled: !!quoteId,
  });
}

// =============================================================================
// Sensor Hooks
// =============================================================================

export function useQuoteSensors(
  quoteNumber: string | undefined,
  invoiceNumbers: string[] | null | undefined = [],
) {
  const normalizedInvoiceNumbers = useMemo(
    () => (Array.isArray(invoiceNumbers) ? invoiceNumbers : []),
    [invoiceNumbers],
  );

  const quoteSensorsQuery = useQuery({
    queryKey: [...quoteKeys.sensors(quoteNumber || ''), 'byQuote'],
    queryFn: () => getSensorsByQuote(quoteNumber!),
    enabled: !!quoteNumber,
    staleTime: 2 * 60 * 1000,
  });

  const invoiceSensorsQuery = useQuery({
    queryKey: [...quoteKeys.sensors(quoteNumber || ''), 'byInvoice', normalizedInvoiceNumbers],
    queryFn: () => getSensorsByInvoices(normalizedInvoiceNumbers),
    enabled: !!quoteNumber && normalizedInvoiceNumbers.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const sensors = useMemo(() => {
    const byQuote = asQuoteSensorArray(quoteSensorsQuery.data);
    const byInvoice = asQuoteSensorArray(invoiceSensorsQuery.data);
    const seen = new Set<string>();
    const merged: QuoteSensor[] = [];

    for (const sensor of [...byQuote, ...byInvoice]) {
      if (!seen.has(sensor._id)) {
        seen.add(sensor._id);
        const filteredInvoices = (sensor.invoices || []).filter(
          (inv) => normalizedInvoiceNumbers.includes(inv)
        );
        merged.push({ ...sensor, invoices: filteredInvoices });
      }
    }
    return merged;
  }, [quoteSensorsQuery.data, invoiceSensorsQuery.data, normalizedInvoiceNumbers]);

  return {
    sensors,
    isLoading: quoteSensorsQuery.isLoading || invoiceSensorsQuery.isLoading,
    refetch: () => {
      quoteSensorsQuery.refetch();
      invoiceSensorsQuery.refetch();
    },
  };
}

export function useQuoteTransmitters(quoteNumber: string | undefined) {
  return useQuery({
    queryKey: [...quoteKeys.sensors(quoteNumber || ''), 'transmitters'],
    queryFn: () => getTransmittersByQuote(quoteNumber!),
    enabled: !!quoteNumber,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSelectableSensors(customerId: string | undefined, params: SensorSelectParams = {}) {
  return useQuery({
    queryKey: quoteKeys.selectableSensors(customerId || '', params as Record<string, unknown>),
    queryFn: () => getSelectableSensors(customerId!, params),
    enabled: !!customerId,
    staleTime: 30 * 1000,
  });
}

export function useSelectableSensorsByAccount(accountId: string | undefined, params: SensorSelectParams = {}) {
  return useQuery({
    queryKey: ['selectable-sensors-by-account', accountId, params],
    queryFn: () => getSelectableSensorsByAccount(accountId!, params),
    enabled: !!accountId,
    staleTime: 30 * 1000,
  });
}

/** Option lists (usergroups / sensorgroups / sites) for the sensor-select dialog filters (#1177). */
export function useAccountSensorFilterOptions(accountId: string | undefined) {
  return useQuery({
    queryKey: ['account-sensor-filter-options', accountId],
    queryFn: () => getSensorFilterOptions(accountId!),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAddSensorToQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sensorId, quoteNumber }: { sensorId: string; quoteNumber: string; sensor?: QuoteSensor }) =>
      addSensorToQuote(sensorId, quoteNumber),
    onSuccess: (_data, variables) => {
      // Optimistically add sensor to cache so the UI updates immediately
      // (V3 refetch may return empty due to org mismatch; this ensures correct UI)
      if (variables.sensor) {
        const key = [...quoteKeys.sensors(variables.quoteNumber), 'byQuote'];
        const existing = queryClient.getQueryData<QuoteSensor[]>(key) || [];
        if (!existing.some(s => s._id === variables.sensor!._id)) {
          queryClient.setQueryData<QuoteSensor[]>(key, [...existing, variables.sensor!]);
        }
      }
      queryClient.invalidateQueries({ queryKey: quoteKeys.sensors(variables.quoteNumber) });
      toast.success('Sensor added to quote');
    },
    onError: (error: unknown) => {
      toast.error('Failed to add sensor', { description: getErrorMessage(error) });
    },
  });
}

export function useRemoveSensorFromQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sensorId, quoteNumber }: { sensorId: string; quoteNumber: string }) =>
      removeSensorFromQuote(sensorId, quoteNumber),
    onSuccess: (_data, variables) => {
      // Optimistically remove sensor from cache
      const key = [...quoteKeys.sensors(variables.quoteNumber), 'byQuote'];
      const existing = queryClient.getQueryData<QuoteSensor[]>(key) || [];
      queryClient.setQueryData<QuoteSensor[]>(key, existing.filter(s => s._id !== variables.sensorId));
      queryClient.invalidateQueries({ queryKey: quoteKeys.sensors(variables.quoteNumber) });
      toast.success('Sensor removed from quote');
    },
    onError: (error: unknown) => {
      toast.error('Failed to remove sensor', { description: getErrorMessage(error) });
    },
  });
}
